import React from 'react';

import { Header, rem, Text } from '@vimeo/bokeh';
import { Speedometer, Database, Users, VideosStack } from '@vimeo/bokeh-icons';
import { translate } from '@vimeo/i18n-runtime';

import { PLANS } from '../../../utils/constants';
import { defaultTemplateProps, PlanData, PlanQuota } from '../../../utils/interface';
import { getPlanQuota, isRepackagingTier } from '../../../utils/utils';
import { BlueHighlight } from '../../common/BlueHighlight';
import { PlanQuotaContainer, StorageQuotaContainer, StyledRow } from './styles';

interface Props {
  planData: PlanData;
  displayMonthlyPlan: boolean;
  modalConfig?: defaultTemplateProps;
  showStorageQuotaModalUpdates?: boolean;
}

const PlanCardQuota = (props: Props) => {
  const { planData, modalConfig, showStorageQuotaModalUpdates } = props;

  const planQuota: PlanQuota | null = getPlanQuota(planData);
  const { entitlements } = planData.metadata;
  const seatOverrideCopy = modalConfig?.planOverrides?.[planData.tier]?.seatCopy;

  const renderValue = (value: string, highlight = false) => {
    if (showStorageQuotaModalUpdates && highlight) {
      return (
        <BlueHighlight>
          <Header size="xl" variant={{ base: 'heading-xs', md: 'heading-sm', xl: 'heading-xs' }}>
            {value}
          </Header>
        </BlueHighlight>
      );
    }

    return <Text variant={{ base: 'body-md', md: 'body-lg', xl: 'body-md' }}>{value}</Text>;
  };

  if (isRepackagingTier(planData.tier)) {
    const teamSeats = entitlements?.params?.teamSeats;
    const managedStorageLimit =
      entitlements?.params?.restrictedVideoStorageLimit ??
      entitlements?.params?.videoStoragePeriodicQuota ??
      '';
    const totalStorage = entitlements?.params?.videoStoragePeriodicQuota ?? '';
    const bandwidth = entitlements?.params?.bandwidth?.periodicQuota ?? '';

    const seatCopy = seatOverrideCopy
      ? `${teamSeats} ${seatOverrideCopy}`
      : (() => {
          switch (planData.tier) {
            case PLANS.PROFESSIONAL:
              return translate({
                singular: '{AMOUNT} users (up to {ADD_USERS})',
                replacements: { AMOUNT: teamSeats, ADD_USERS: 20 },
              });
            case PLANS.STUDIO:
              return translate({
                singular: '{AMOUNT} users (up to {ADD_USERS})',
                replacements: { AMOUNT: teamSeats, ADD_USERS: 200 },
              });
            case PLANS.PRODUCTION:
              return translate({
                singular: '{AMOUNT} users (up to {ADD_USERS})',
                replacements: { AMOUNT: teamSeats, ADD_USERS: translate('Unlimited') },
              });
            default:
              return translate({
                singular: '{AMOUNT} user',
                plural: '{AMOUNT} users',
                count: teamSeats ?? 0,
                replacements: { AMOUNT: teamSeats },
              });
          }
        })();

    const repackagingFeatureRows = [
      {
        icon: (
          <Users
            h={{ base: rem(16), md: rem(20), xl: rem(16) }}
            w={{ base: rem(16), md: rem(20), xl: rem(16) }}
          />
        ),
        value: seatCopy,
      },
      {
        icon: (
          <Database
            h={{ base: rem(16), md: rem(20), xl: rem(16) }}
            w={{ base: rem(16), md: rem(20), xl: rem(16) }}
          />
        ),
        value: translate({
          singular: '{AMOUNT} managed storage for embeddable and non-Public videos',
          replacements: { AMOUNT: managedStorageLimit },
        }),
        highlight: true,
      },
      ...(planData.tier === PLANS.CREATOR
        ? [
            {
              icon: (
                <Database
                  h={{ base: rem(16), md: rem(20), xl: rem(16) }}
                  w={{ base: rem(16), md: rem(20), xl: rem(16) }}
                />
              ),
              value: translate({
                singular: '{AMOUNT} total storage (including public, non-embedded videos)',
                replacements: { AMOUNT: totalStorage },
              }),
            },
          ]
        : []),
      {
        icon: (
          <Speedometer
            h={{ base: rem(16), md: rem(20), xl: rem(16) }}
            w={{ base: rem(16), md: rem(20), xl: rem(16) }}
          />
        ),
        value: translate({
          singular: '{AMOUNT} monthly bandwidth',
          replacements: { AMOUNT: bandwidth },
        }),
      },
    ];

    return (
      <PlanQuotaContainer>
        <StorageQuotaContainer>
          {repackagingFeatureRows.map((row, index) => (
            <StyledRow key={`${planData.tier}-quota-${index}`}>
              <div>{row.icon}</div>
              {renderValue(row.value, row.highlight)}
            </StyledRow>
          ))}
        </StorageQuotaContainer>
      </PlanQuotaContainer>
    );
  }

  if (!planQuota) return null;

  const { getSeatCount, getVideoCount, getStorageQuota, getBandwidth } = planQuota;
  const isStorageQuota: boolean = entitlements?.params?.videoStorageQuotaUnit === 'video_size';

  return (
    <PlanQuotaContainer>
      <StorageQuotaContainer>
        <StyledRow>
          <div>
            <Users
              h={{ base: rem(16), md: rem(20), xl: rem(16) }}
              w={{ base: rem(16), md: rem(20), xl: rem(16) }}
            />
          </div>
          <Text variant={{ base: 'body-md', md: 'body-lg', xl: 'body-md' }}>
            {seatOverrideCopy
              ? `${entitlements?.params?.teamSeats} ${seatOverrideCopy}`
              : getSeatCount(entitlements?.params?.teamSeats, true)}
          </Text>
        </StyledRow>
        <StyledRow>
          {isStorageQuota ? (
            <Database
              h={{ base: rem(16), md: rem(20), xl: rem(16) }}
              w={{ base: rem(16), md: rem(20), xl: rem(16) }}
            />
          ) : (
            <VideosStack
              h={{ base: rem(16), md: rem(20), xl: rem(16) }}
              w={{ base: rem(16), md: rem(20), xl: rem(16) }}
            />
          )}
          {showStorageQuotaModalUpdates ? (
            <BlueHighlight>
              <Header
                size="xl"
                variant={{ base: 'heading-xs', md: 'heading-sm', xl: 'heading-xs' }}
              >
                {isStorageQuota
                  ? getStorageQuota(entitlements?.params?.videoStoragePeriodicQuota)
                  : getVideoCount(entitlements?.params?.videoStoragePeriodicQuota)}
              </Header>
            </BlueHighlight>
          ) : (
            renderValue(
              isStorageQuota
                ? getStorageQuota(entitlements?.params?.videoStoragePeriodicQuota)
                : getVideoCount(entitlements?.params?.videoStoragePeriodicQuota),
            )
          )}
        </StyledRow>
        {getBandwidth && planData.metadata.isBandwidthProduct && (
          <StyledRow>
            <div>
              <Speedometer
                h={{ base: rem(16), md: rem(20), xl: rem(16) }}
                w={{ base: rem(16), md: rem(20), xl: rem(16) }}
              />
            </div>
            {renderValue(getBandwidth(entitlements?.params?.bandwidth?.periodicQuota))}
          </StyledRow>
        )}
      </StorageQuotaContainer>
    </PlanQuotaContainer>
  );
};

export default PlanCardQuota;
